import { PagesPermissions } from '@/models/Permissions/enum';
import { fetchBulkTransactionTitlesStructured } from '@/pages/HedgeAccountingAdminV2/store/thunks';
import { BulkTransactionTitle } from '@/pages/HedgeAccountingAdminV2/store/types';
import { TransactionIdLabel } from '@/pages/Pricers/components/ButtonActions/styles';
import {
  tradeStatusColors,
  tradeStatusLabels,
  transactionTypeColors,
  transactionTypeLabels,
} from '@/pages/TradeCenter/components/TransactionTableColumns/utils';
import { TransactionTypes } from '@/pages/TradeCenter/enums/TransactionTypes';
import {
  useAppDispatch,
  useAppSelector,
  useUserHasAtLeastOnePermission,
} from '@store/hooks';
import { RootState } from '@store/store';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';

import { ICONS_TYPES } from '../Icon';

import {
  ButtonWrapper,
  DetailAction,
  DetailActionIcon,
  DetailActionLabel,
  DetailActions,
  DetailCard,
  DetailHeader,
  DetailHeaderText,
  DetailStat,
  DetailStatLabel,
  DetailStatValue,
  DetailStats,
  DetailString,
  DetailTitle,
  DismissButton,
  EmptyState,
  InputRow,
  LoadingState,
  LoadingTradeId,
  Overlay,
  Panel,
  PropertiesIcon,
  ResultBadge,
  ResultMain,
  ResultPrimary,
  ResultRow,
  ResultSecondary,
  ResultsScroll,
  Root,
  SearchInput,
  SearchInputIcon,
  SectionDot,
  SectionHeader,
  ShortcutKey,
  TypeTag,
} from './styles';
import {
  DetectedQueryType,
  EntityResult,
  NavbarSearchPopoverProps,
} from './types';

const MAX_RESULTS = 20;

const detectQueryType = (q: string): DetectedQueryType | null => {
  const trimmed = q.trim();
  if (!trimmed) return null;
  if (/^\d+$/.test(trimmed)) return 'trade';
  if (/\d/.test(trimmed) && /[a-zA-Z]/.test(trimmed)) return 'mixed';
  return 'entity';
};

const highlightMatch = (text: string, query: string): React.ReactNode => {
  if (!text || !query) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <em>{text.slice(idx, idx + query.length)}</em>
      {text.slice(idx + query.length)}
    </>
  );
};

const typeTagLabel = (type: DetectedQueryType): string => {
  if (type === 'trade') return 'TRADE ID';
  if (type === 'mixed') return 'ALL';
  return 'ENTITY';
};

const NavbarSearchPopover = ({ className }: NavbarSearchPopoverProps) => {
  const dispatch = useAppDispatch();
  const inputRef = useRef<HTMLInputElement>(null);
  const reqIdRef = useRef(0);
  const titlesReqIdRef = useRef(0);
  const tradeCacheRef = useRef<Map<number, BulkTransactionTitle>>(new Map());

  const companyEntities = useAppSelector(
    (state: RootState) => state.companyEntity.entityList,
  );
  const activeTransactionIds = useAppSelector(
    (state: RootState) => state.global.activeTransactionIds,
  );

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedTrade, setSelectedTrade] =
    useState<BulkTransactionTitle | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [loadingTradeId, setLoadingTradeId] = useState<number | null>(null);
  const [focusIdx, setFocusIdx] = useState(-1);
  const [tradeTitles, setTradeTitles] = useState<
    Map<number, BulkTransactionTitle>
  >(new Map());
  // IDs that came back from activeTransactionIds but don't resolve to a real
  // trade title — pruned from suggestions so they stop being offered.
  const noTitleIdsRef = useRef<Set<number>>(new Set());
  const [noTitleVersion, setNoTitleVersion] = useState(0);

  const hasPricerPermission = useUserHasAtLeastOnePermission([
    PagesPermissions.irPricer,
    PagesPermissions.fxPricer,
    PagesPermissions.inflationPricer,
    PagesPermissions.commodityPricer,
  ]);

  const hasHeadgeAccountingPermission = useUserHasAtLeastOnePermission([
    PagesPermissions.hedgeAccounting,
  ]);

  const hasJournalEntriesPermission = useUserHasAtLeastOnePermission([
    PagesPermissions.journalEntries,
  ]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => inputRef.current?.focus());
    } else {
      setQuery('');
      setSelectedTrade(null);
      setFocusIdx(-1);
    }
  }, [open]);

  const entityResults = useMemo<EntityResult[]>(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return (companyEntities ?? [])
      .filter(
        (e) =>
          e.comp_entity_name?.toLowerCase().includes(q) ||
          e.company_name?.toLowerCase().includes(q),
      )
      .slice(0, MAX_RESULTS)
      .map((e) => ({
        comp_entity_id: e.comp_entity_id,
        comp_entity_name: e.comp_entity_name,
        company_name: e.company_name,
      }));
  }, [companyEntities, query]);

  // Trade ID search — instant local filter from activeTransactionIds,
  // excluding IDs we've learned don't resolve to a real trade title.
  const tradeIdResults = useMemo<number[]>(() => {
    const q = query.trim();
    if (!q || !/^\d+$/.test(q)) return [];
    return activeTransactionIds
      .filter((id) => String(id).includes(q) && !noTitleIdsRef.current.has(id))
      .slice(0, MAX_RESULTS);
    // noTitleVersion re-runs the filter after we prune an unresolved ID
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTransactionIds, query, noTitleVersion]);

  // Debounced bulk fetch of titles for the listed trade IDs so we can show
  // nice titles inline while the user is still typing/autocompleting.
  useEffect(() => {
    const missing = tradeIdResults.filter(
      (id) => !tradeCacheRef.current.has(id),
    );
    if (missing.length === 0) return;

    const handle = setTimeout(async () => {
      const reqId = ++titlesReqIdRef.current;
      try {
        const titles = await dispatch(
          fetchBulkTransactionTitlesStructured({
            transaction_ids: missing,
            silent: true,
          }),
        ).unwrap();
        if (reqId !== titlesReqIdRef.current) return;

        // A trade is only valid if it came back with a usable title and no
        // error — the API returns success with an `error` entry for IDs it
        // can't resolve (e.g. "ERROR on getting transaction title").
        const resolved = new Set<number>();
        titles.forEach((t) => {
          if (t.error || !t.title) return;
          tradeCacheRef.current.set(t.transaction_id, t);
          resolved.add(t.transaction_id);
        });

        // Hide any requested ID we couldn't resolve a real title for.
        let pruned = false;
        missing.forEach((id) => {
          if (!resolved.has(id)) {
            noTitleIdsRef.current.add(id);
            pruned = true;
          }
        });
        setTradeTitles(new Map(tradeCacheRef.current));
        if (pruned) setNoTitleVersion((v) => v + 1);
      } catch {
        if (reqId !== titlesReqIdRef.current) return;
        // The bulk call failed. If it was a single ID, the failure is
        // attributable to that ID, so prune it; otherwise leave the batch
        // alone (rows just fall back to showing the raw trade ID).
        if (missing.length === 1) {
          noTitleIdsRef.current.add(missing[0]);
          setNoTitleVersion((v) => v + 1);
        }
      }
    }, 300);

    return () => clearTimeout(handle);
  }, [tradeIdResults, dispatch]);

  const close = useCallback(() => setOpen(false), []);

  const handleSelectEntity = useCallback((entity: EntityResult) => {
    window.open(`/portfolio/entity/${entity.comp_entity_id}`, '_blank');
  }, []);

  // On trade select — fetch full details for that single trade
  const handleSelectTradeId = useCallback(
    async (transactionId: number) => {
      const cached = tradeCacheRef.current.get(transactionId);
      if (cached) {
        setSelectedTrade(cached);
        return;
      }

      setDetailLoading(true);
      setLoadingTradeId(transactionId);
      const reqId = ++reqIdRef.current;
      try {
        const titles = await dispatch(
          fetchBulkTransactionTitlesStructured({
            transaction_ids: [transactionId],
            silent: true,
          }),
        ).unwrap();
        if (reqId !== reqIdRef.current) return;
        const trade = titles[0];
        if (trade && !trade.error && trade.title) {
          tradeCacheRef.current.set(transactionId, trade);
          setSelectedTrade(trade);
        } else {
          // Couldn't resolve a real title — hide this ID from suggestions.
          noTitleIdsRef.current.add(transactionId);
          setNoTitleVersion((v) => v + 1);
          setSelectedTrade(null);
        }
      } catch {
        if (reqId === reqIdRef.current) setSelectedTrade(null);
      } finally {
        if (reqId === reqIdRef.current) setDetailLoading(false);
      }
    },
    [dispatch],
  );

  // const handleSelectTrade = useCallback((trade: BulkTransactionTitle) => {
  //   setSelectedTrade(trade);
  // }, []);

  const handleDismissTrade = useCallback(() => {
    setSelectedTrade(null);
    setQuery('');
    setFocusIdx(-1);
    inputRef.current?.focus();
  }, []);

  const handleTradeAction = useCallback((path: string) => {
    window.open(path, '_blank');
  }, []);

  const flatResults = useMemo(
    () => [
      ...entityResults.map((e) => ({ kind: 'entity' as const, data: e })),
      ...tradeIdResults.map((id) => ({ kind: 'trade' as const, data: id })),
    ],
    [entityResults, tradeIdResults],
  );

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusIdx((idx) =>
          flatResults.length === 0
            ? -1
            : Math.min(flatResults.length - 1, idx + 1),
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusIdx((idx) => Math.max(0, idx - 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (focusIdx >= 0 && focusIdx < flatResults.length) {
          const item = flatResults[focusIdx];
          if (item.kind === 'entity') {
            handleSelectEntity(item.data as EntityResult);
          } else {
            handleSelectTradeId(item.data as number);
          }
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        if (selectedTrade) {
          handleDismissTrade();
        } else {
          close();
        }
      }
    },
    [
      flatResults,
      focusIdx,
      selectedTrade,
      handleSelectEntity,
      handleDismissTrade,
      close,
      handleSelectTradeId,
    ],
  );

  const detectedType = detectQueryType(query);
  const trimmedQuery = query.trim();
  const showNoResults =
    !!trimmedQuery &&
    !detailLoading &&
    entityResults.length === 0 &&
    tradeIdResults.length === 0;

  const tradeBadgeText = (trade: BulkTransactionTitle): string =>
    trade.instrument_type?.short_description ??
    trade.instrument_type?.name ??
    '';

  const modal = open ? (
    <Overlay onMouseDown={close}>
      <Panel onMouseDown={(e) => e.stopPropagation()}>
        <InputRow>
          <SearchInputIcon icon={ICONS_TYPES.Search} size={16} />
          <SearchInput
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setFocusIdx(-1);
              if (selectedTrade) setSelectedTrade(null);
            }}
            onKeyDown={handleInputKeyDown}
            placeholder="Search for entities by name or trades by trade ID"
            autoComplete="off"
            spellCheck={false}
          />
          {detectedType ? (
            <TypeTag kind={detectedType}>{typeTagLabel(detectedType)}</TypeTag>
          ) : (
            <ShortcutKey>Ctrl+K</ShortcutKey>
          )}
        </InputRow>

        {detailLoading && (
          <LoadingState>
            Loading trade details for{' '}
            <LoadingTradeId>#{loadingTradeId}</LoadingTradeId>…
          </LoadingState>
        )}

        {trimmedQuery && !selectedTrade && !detailLoading && (
          <ResultsScroll>
            {entityResults.length > 0 && (
              <>
                <SectionHeader>
                  <SectionDot kind="entity" />
                  Entities
                </SectionHeader>
                {entityResults.map((entity, i) => (
                  <ResultRow
                    key={`e-${entity.comp_entity_id}`}
                    focused={focusIdx === i}
                    onMouseEnter={() => setFocusIdx(i)}
                    onClick={() => handleSelectEntity(entity)}>
                    <ResultMain>
                      <ResultPrimary>
                        {highlightMatch(entity.comp_entity_name, trimmedQuery)}
                      </ResultPrimary>
                      <ResultSecondary>
                        {highlightMatch(entity.company_name, trimmedQuery)}
                      </ResultSecondary>
                    </ResultMain>
                    <ResultBadge kind="entity">ENTITY</ResultBadge>
                  </ResultRow>
                ))}
              </>
            )}

            {tradeIdResults.length > 0 && (
              <>
                <SectionHeader>
                  <SectionDot kind="trade" />
                  Trades
                </SectionHeader>
                {tradeIdResults.map((id, i) => {
                  const idx = entityResults.length + i;
                  const title = tradeTitles.get(id)?.title;
                  return (
                    <ResultRow
                      key={`t-${id}`}
                      focused={focusIdx === idx}
                      onMouseEnter={() => setFocusIdx(idx)}
                      onClick={() => handleSelectTradeId(id)}>
                      <ResultMain>
                        <ResultPrimary>
                          {highlightMatch(String(id), trimmedQuery)}
                        </ResultPrimary>
                        {title && <ResultSecondary>{title}</ResultSecondary>}
                      </ResultMain>
                      <ResultBadge kind="trade">TRADE</ResultBadge>
                    </ResultRow>
                  );
                })}
              </>
            )}

            {detailLoading && (
              <LoadingState>Loading trade details…</LoadingState>
            )}

            {showNoResults && (
              <EmptyState>
                No results for &ldquo;{trimmedQuery}&rdquo;
              </EmptyState>
            )}
          </ResultsScroll>
        )}

        {selectedTrade && (
          <DetailCard>
            <DetailHeader>
              <DetailHeaderText>
                <DetailTitle>
                  {selectedTrade.transaction_id}
                  {selectedTrade.company_entity?.name
                    ? ` · ${selectedTrade.company_entity.name}`
                    : ''}
                </DetailTitle>
                <DetailString>{selectedTrade.title}</DetailString>
              </DetailHeaderText>
              <DismissButton onClick={handleDismissTrade}>
                ✕ dismiss
              </DismissButton>
            </DetailHeader>
            <DetailStats>
              <DetailStat>
                <DetailStatValue>
                  {tradeBadgeText(selectedTrade)}
                </DetailStatValue>
                <DetailStatLabel>Instrument</DetailStatLabel>
              </DetailStat>
              <DetailStat>
                {(() => {
                  const status =
                    selectedTrade.internal_details?.transaction_status?.status;
                  const typeId = selectedTrade.internal_details
                    ?.transaction_type?.id as TransactionTypes;
                  const statusColor = status
                    ? tradeStatusColors[status]
                    : undefined;
                  const statusLabel = status
                    ? tradeStatusLabels[status]
                    : undefined;
                  const typeColor = typeId
                    ? transactionTypeColors[typeId]
                    : undefined;
                  const typeLabel = typeId
                    ? transactionTypeLabels[typeId]
                    : undefined;
                  return (
                    <>
                      {typeColor && typeLabel && (
                        <TransactionIdLabel color={typeColor}>
                          {typeLabel}
                        </TransactionIdLabel>
                      )}
                      {statusColor && statusLabel && (
                        <TransactionIdLabel color={statusColor}>
                          {statusLabel}
                        </TransactionIdLabel>
                      )}
                    </>
                  );
                })()}
                <DetailStatLabel>Status</DetailStatLabel>
              </DetailStat>
            </DetailStats>
            <DetailActions>
              {hasPricerPermission && (
                <DetailAction
                  onClick={() =>
                    handleTradeAction(
                      `/hedging-center/pricer/${selectedTrade.transaction_id}`,
                    )
                  }>
                  <DetailActionIcon icon={ICONS_TYPES.TrendingUp} size={20} />
                  <DetailActionLabel>Pricer</DetailActionLabel>
                </DetailAction>
              )}
              <DetailAction
                $primary
                onClick={() =>
                  handleTradeAction(
                    `/portfolio/transaction/${selectedTrade.transaction_id}`,
                  )
                }>
                <DetailActionIcon
                  $primary
                  icon={ICONS_TYPES.TransactionDollar}
                  size={20}
                />
                <DetailActionLabel $primary>Transaction</DetailActionLabel>
              </DetailAction>
              {hasJournalEntriesPermission && (
                <DetailAction
                  onClick={() =>
                    handleTradeAction(
                      `/journal-entries?tradeIds=${selectedTrade.transaction_id}`,
                    )
                  }>
                  <DetailActionIcon icon={ICONS_TYPES.Notebook} size={20} />
                  <DetailActionLabel>Journal</DetailActionLabel>
                </DetailAction>
              )}

              {hasHeadgeAccountingPermission && (
                <DetailAction
                  onClick={() =>
                    handleTradeAction(
                      `/manage-hedge-accounting/${selectedTrade.transaction_id}`,
                    )
                  }>
                  <DetailActionIcon icon={ICONS_TYPES.ShieldCheck} size={20} />
                  <DetailActionLabel>HA Mgmt</DetailActionLabel>
                </DetailAction>
              )}
            </DetailActions>
          </DetailCard>
        )}
      </Panel>
    </Overlay>
  ) : null;

  return (
    <Root className={className}>
      <ButtonWrapper onClick={() => setOpen(true)} open={open}>
        <PropertiesIcon
          icon={ICONS_TYPES.Search}
          size={18}
          open={open}
          rotate={90}
        />
      </ButtonWrapper>
      {modal && ReactDOM.createPortal(modal, document.body)}
    </Root>
  );
};

export default NavbarSearchPopover;
